import React, { useState, useEffect } from 'react';

const BuyBranchesModal = ({ isOpen, onClose, companyProfile, token, onSuccess }) => {
    const [extraCount, setExtraCount] = useState(1);
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState('');
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState('');

    const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:5000'
        : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

    const currentLimit = companyProfile?.branch_limit || 1;
    const currentPlan = (companyProfile?.subscription_plan || 'starter').toLowerCase();
    const targetLimit = currentLimit + extraCount;

    useEffect(() => {
        if (!isOpen) return;

        let active = true;
        const fetchQuote = async () => {
            setQuoteLoading(true);
            setQuoteError('');
            try {
                const res = await fetch(
                    `${API_URL}/api/payment/upgrade-quote?target_plan=${currentPlan}&target_billing_period=current_cycle&target_branch_limit=${targetLimit}&is_branch_only=true&extra_count=${extraCount}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                if (active) {
                    if (data.success) {
                        setQuote(data);
                    } else {
                        setQuoteError(data.message || 'Unable to calculate upgrade price.');
                        setQuote(null);
                    }
                }
            } catch (err) {
                if (active) {
                    setQuoteError('Network error. Please try again.');
                    setQuote(null);
                }
            } finally {
                if (active) {
                    setQuoteLoading(false);
                }
            }
        };

        fetchQuote();

        return () => {
            active = false;
        };
    }, [isOpen, targetLimit, currentPlan, API_URL, token, extraCount]);

    const handleProceedToPayment = async () => {
        if (!quote) return;
        setPaymentLoading(true);
        setPaymentError('');

        try {
            const body = {
                plan_type: currentPlan,
                amount: quote.prorated_amount,
                currency: 'INR',
                billing_period: 'current_cycle',
                branch_limit: targetLimit,
                is_upgrade: true,
                upgraded_from: currentPlan,
                is_renewal: false
            };

            const orderRes = await fetch(`${API_URL}/api/payment/create-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            const orderData = await orderRes.json();
            if (!orderData.success) throw new Error(orderData.message || 'Failed to create order');

            if (orderData.gateway === 'razorpay') {
                const options = {
                    key: orderData.razorpay_key,
                    amount: orderData.amount,
                    currency: orderData.currency,
                    name: 'RedSorm',
                    description: `Buy ${extraCount} Extra Branch/Outlet(s)`,
                    order_id: orderData.razorpay_order_id,
                    handler: async (response) => {
                        setPaymentLoading(true);
                        try {
                            const verifyRes = await fetch(`${API_URL}/api/payment/verify`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                    order_id: orderData.order_id,
                                    razorpay_payment_id: response.razorpay_payment_id,
                                    razorpay_order_id: response.razorpay_order_id,
                                    razorpay_signature: response.razorpay_signature
                                })
                            });
                            const verifyData = await verifyRes.json();
                            if (verifyData.success) {
                                onClose();
                                if (onSuccess) onSuccess();
                                window.location.href = `/payment/status?order_id=${orderData.order_id}`;
                            } else {
                                throw new Error(verifyData.message || 'Payment verification failed');
                            }
                        } catch (vErr) {
                            setPaymentError(vErr.message);
                            setPaymentLoading(false);
                        }
                    },
                    prefill: {
                        name: orderData.customer_name,
                        email: orderData.customer_email,
                        contact: orderData.customer_phone
                    },
                    theme: { color: '#6366f1' },
                    modal: {
                        ondismiss: () => setPaymentLoading(false)
                    }
                };
                const rzp = new window.Razorpay(options);
                rzp.open();
            } else {
                // Cashfree Fallback
                const { load } = await import('@cashfreepayments/cashfree-js');
                const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
                const cashfree = await load({ mode: isProduction ? 'production' : 'sandbox' });
                cashfree.checkout({ paymentSessionId: orderData.payment_session_id, redirectTarget: '_self' });
            }
        } catch (err) {
            setPaymentError(err.message || 'Something went wrong. Please try again.');
            setPaymentLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-70 backdrop-blur-sm" onClick={onClose} />

            {/* Modal Box */}
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in-up flex flex-col">
                
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                            <i className="fas fa-store text-white text-lg" />
                        </div>
                        <div>
                            <h2 className="text-white font-bold text-lg leading-tight">Buy Extra Outlets</h2>
                            <p className="text-white/80 text-xs">Add more branches to your {currentPlan === 'premium' ? 'Premium' : 'Starter'} plan</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white transition">
                        <i className="fas fa-times text-xl" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
                    
                    {/* Error Alerts */}
                    {paymentError && (
                        <div className="bg-rose-50 border border-rose-100 text-rose-600 p-4 rounded-2xl text-xs font-bold flex items-center gap-2">
                            <i className="fas fa-exclamation-circle text-sm" />
                            {paymentError}
                        </div>
                    )}
                    {quoteError && (
                        <div className="bg-rose-50 border border-rose-100 text-rose-600 p-4 rounded-2xl text-xs font-bold flex items-center gap-2">
                            <i className="fas fa-exclamation-circle text-sm" />
                            {quoteError}
                        </div>
                    )}

                    {/* Branch Limit Info */}
                    <div className="bg-gray-50 border border-gray-150 rounded-2xl p-4 flex justify-between items-center text-center">
                        <div>
                            <span className="text-xs font-medium text-gray-500 block mb-1">Current Outlets</span>
                            <span className="text-xl font-black text-gray-800">{currentLimit} {currentLimit === 1 ? 'Outlet' : 'Outlets'}</span>
                        </div>
                        <div className="w-px h-10 bg-gray-200" />
                        <div>
                            <span className="text-xs font-medium text-purple-500 block mb-1">New Outlets Count</span>
                            <span className="text-xl font-black text-purple-700">{targetLimit} {targetLimit === 1 ? 'Outlet' : 'Outlets'}</span>
                        </div>
                    </div>

                    {/* Selector */}
                    <div className="space-y-3">
                        <label className="block text-sm font-black text-purple-950">Number of Extra Outlets to Buy</label>
                        <div className="flex items-center gap-4 justify-center">
                            <button
                                type="button"
                                onClick={() => setExtraCount(prev => Math.max(1, prev - 1))}
                                disabled={extraCount <= 1}
                                className="w-12 h-12 bg-white border-2 border-purple-200 rounded-xl flex items-center justify-center text-purple-700 font-bold hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition active:scale-95"
                            >
                                <i className="fas fa-minus"></i>
                            </button>
                            <span className="text-2xl font-black text-purple-900 w-12 text-center">{extraCount}</span>
                            <button
                                type="button"
                                onClick={() => setExtraCount(prev => prev + 1)}
                                className="w-12 h-12 bg-white border-2 border-purple-200 rounded-xl flex items-center justify-center text-purple-700 font-bold hover:bg-purple-50 shadow-sm transition active:scale-95"
                            >
                                <i className="fas fa-plus"></i>
                            </button>
                        </div>
                    </div>

                    {/* Quote display */}
                    {quoteLoading ? (
                        <div className="flex flex-col items-center justify-center py-6 gap-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600"></div>
                            <span className="text-xs font-bold text-purple-600 animate-pulse">Calculating prorated amount...</span>
                        </div>
                    ) : quote ? (
                        <div className="bg-purple-50/50 border border-purple-100 rounded-2xl p-4 space-y-3">
                            <h4 className="text-xs font-black text-purple-950 uppercase tracking-wider">Payment Breakdown</h4>
                            <div className="space-y-2 text-xs">
                                {quote.subscription_end_date && (
                                    <div className="flex justify-between text-purple-900/80 font-medium">
                                        <span>Current Expiry Date:</span>
                                        <span className="font-extrabold text-purple-950">
                                            {new Date(quote.subscription_end_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between text-purple-900/80 font-medium">
                                    <span>Prorated Days Remaining:</span>
                                    <span className="font-extrabold text-purple-950">{quote.days_remaining} / {quote.days_total} Days</span>
                                </div>
                                {quote.unused_credit > 0 ? (
                                    <>
                                        <div className="flex justify-between text-purple-900/80 font-medium border-t border-purple-100/50 pt-2">
                                            <span>Unused Credit:</span>
                                            <span className="font-extrabold text-purple-950">₹{quote.unused_credit}</span>
                                        </div>
                                        <div className="flex justify-between text-purple-900/80 font-medium">
                                            <span>Prorated Cost (with extra outlets):</span>
                                            <span className="font-extrabold text-purple-950">₹{quote.new_plan_prorated_cost}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex justify-between text-purple-900/80 font-medium border-t border-purple-100/50 pt-2">
                                        <span>Prorated Cost for Extra Outlets:</span>
                                        <span className="font-extrabold text-purple-950">₹{quote.prorated_amount}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm text-purple-950 font-black border-t-2 border-dashed border-purple-200 pt-2">
                                    <span>Total Amount to Pay:</span>
                                    <span className="text-purple-700 text-lg">₹{quote.prorated_amount}</span>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Notice */}
                    <div className="bg-amber-50/80 border border-amber-100 text-amber-800 p-4 rounded-2xl text-[11px] leading-relaxed flex gap-3 font-medium">
                        <i className="fas fa-info-circle text-amber-500 text-base flex-shrink-0 mt-0.5" />
                        <div>
                            This purchase is prorated. You are only paying for the remaining days of your current billing cycle. After your current plan cycle ends, the renewal price will include these extra outlets.
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-150 flex items-center justify-between gap-4 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-xl border border-gray-300 font-bold text-gray-700 hover:bg-gray-100 transition text-xs"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleProceedToPayment}
                        disabled={quoteLoading || !quote || paymentLoading}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl transition text-xs shadow-md shadow-indigo-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {paymentLoading ? (
                            <>
                                <i className="fas fa-spinner fa-spin" />
                                Processing Checkout...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-credit-card" />
                                Pay {quote ? `₹${quote.prorated_amount}` : '...'} Now
                            </>
                        )}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default BuyBranchesModal;
